import { useEffect, useRef, useState } from 'react';
import { isEqual, deepClone } from '../../../../util/util';
import { useBlockNavigationWhenDirty } from '../../../../hooks/useBlockNavigationWhenDirty';
import { useAccountingFirmEditGeneralSettingsContext } from '../General/context/useContext';
import { AccessLevelRef } from '../AccessLevels/types';

interface AccountingFirmSettingsObserver {
  isFormModified: boolean;
  isGeneralModified: boolean;
  isAccessLevelModified: boolean;
  revertGeneralChanges: () => void;
}

const useAccountingFirmSettingsObserver = (accessLevelRef: AccessLevelRef | null): AccountingFirmSettingsObserver => {
  const [isGeneralModified, setIsGeneralModified] = useState(false);
  const [isAccessLevelModified, setIsAccessLevelModified] = useState(false);
  const [initialGeneralState, setInitialGeneralState] = useState<any>(null);

  const initialAccessLevelsRef = useRef({
    allowAdvisorAccess: false,
    allowCorrectoAccess: false,
    seniorConsultants: [],
  });

  const {
    companyDetails,
    addressDetails,
    contractEffectiveFrom,
    contractEmail,
    invoiceCostCenter,
    combinations,
    firmDetails,
    signatoryDetails,
    allowAdvisorRemoteAccess,
    allowCorrectoRemoteAccess,
    accessConsultants,
    replaceCompanyDetails,
    replaceAddress,
    updateContractEffectiveInput,
    replaceCombinations,
  } = useAccountingFirmEditGeneralSettingsContext();

  const isFormModified = isGeneralModified || isAccessLevelModified;

  useEffect(() => {
    if (firmDetails && !initialGeneralState) {
      setInitialGeneralState({
        companyDetails: deepClone({
          name: firmDetails.name,
          organizationNumber: firmDetails.organizationNumber,
          status: firmDetails.status,
          contractNumber: firmDetails.contractNumber,
          resellerAgreement: firmDetails.resellerAgreement,
        }),
        addressDetails: deepClone(firmDetails.address),
        contractEffectiveFrom: firmDetails.contractEffectiveFrom,
        contractEmail: firmDetails.invoiceEmailAddress || '',
        invoiceCostCenter: firmDetails.invoicingCostCenter,
        signatoryDetails: deepClone(firmDetails.authorizedSignatory || []),
        combinations: deepClone(firmDetails.combinations || []),
      });
    }

    if (firmDetails) {
      initialAccessLevelsRef.current = {
        allowAdvisorAccess: firmDetails.allowAdvisorAccess || false,
        allowCorrectoAccess: firmDetails.allowCorrectoAccess || false,
        seniorConsultants: firmDetails.seniorConsultants || [],
      };
    }
  }, [firmDetails]);

  useEffect(() => {
    if (!initialGeneralState) return;

    const currentState = {
      company: {
        name: companyDetails.name,
        contractNumber: companyDetails.contractNumber,
        organizationNumber: companyDetails.organizationNumber,
        status: companyDetails.status,
        resellerAgreement: companyDetails.resellerAgreement,
      },
      address: addressDetails,
      contractEffectiveFrom: contractEffectiveFrom,
      combinations: combinations.map((item) => {
        return {
          signee: item.signee,
          authorizedSignatory: item.authorizedSignatory.map((signatory) => {
            return {
              personalNumber: signatory.personalNumber,
              firstName: signatory.firstName,
              lastName: signatory.lastName,
              position: signatory.position,
              email: signatory.email,
            };
          }),
        };
      }),
    };

    const lastState = {
      address: initialGeneralState.addressDetails,
      company: initialGeneralState.companyDetails,
      contractEffectiveFrom: initialGeneralState.contractEffectiveFrom,
      combinations: (initialGeneralState.combinations || []).map((item) => {
        return {
          signee: item.signee,
          authorizedSignatory: item.authorizedSignatory.map((signatory) => {
            return {
              personalNumber: signatory.personalNumber,
              firstName: signatory.firstName,
              lastName: signatory.lastName,
              position: signatory.position,
              email: signatory.email,
            };
          }),
        };
      }),
    };

    const isAddressModified = isEqual(lastState.address, currentState.address);
    const isCompanyDetailsModified = isEqual(lastState.company, currentState.company);
    const isCombinationsModified = isEqual(lastState.combinations, currentState.combinations);
    const isContractEffectiveFromModified = isEqual(
      lastState.contractEffectiveFrom,
      currentState.contractEffectiveFrom,
    );

    const isModified =
      !isAddressModified || !isCompanyDetailsModified || !isCombinationsModified || !isContractEffectiveFromModified;

    setIsGeneralModified(isModified);
  }, [
    initialGeneralState,
    companyDetails,
    addressDetails,
    contractEffectiveFrom,
    contractEmail,
    invoiceCostCenter,
    signatoryDetails,
    combinations,
  ]);

  useEffect(() => {
    const currentState = {
      allowAdvisorAccess: allowAdvisorRemoteAccess,
      allowCorrectoAccess: allowCorrectoRemoteAccess,
      seniorConsultants: accessConsultants,
    };

    const lastState = {
      allowAdvisorAccess: initialAccessLevelsRef.current?.allowAdvisorAccess || false,
      allowCorrectoAccess: initialAccessLevelsRef.current?.allowCorrectoAccess || false,
      seniorConsultants: initialAccessLevelsRef.current?.seniorConsultants || [],
    };

    const isAdvisorRemoteAccessModified = isEqual(lastState.allowAdvisorAccess, currentState.allowAdvisorAccess);
    const isCorrectoRemoteAccessModified = isEqual(lastState.allowCorrectoAccess, currentState.allowCorrectoAccess);
    const isAccessConsultantsModified = isEqual(lastState.seniorConsultants, currentState.seniorConsultants);

    const isModified =
      !isAdvisorRemoteAccessModified || !isCorrectoRemoteAccessModified || !isAccessConsultantsModified;

    setIsAccessLevelModified(isModified);
  }, [allowAdvisorRemoteAccess, allowCorrectoRemoteAccess, accessConsultants]);

  const revertGeneralChanges = () => {
    if (!initialGeneralState || !firmDetails) return;

    replaceCompanyDetails(deepClone(initialGeneralState.companyDetails));
    replaceAddress(deepClone(initialGeneralState.addressDetails));
    updateContractEffectiveInput(initialGeneralState.contractEffectiveFrom);
    replaceCombinations(deepClone(initialGeneralState.combinations));
  };

  useBlockNavigationWhenDirty(isFormModified);

  return {
    isFormModified,
    isGeneralModified,
    isAccessLevelModified,
    revertGeneralChanges,
  };
};

export default useAccountingFirmSettingsObserver;
